import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { FileText } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";
import { gerarRelatorioEstoquePDF } from "@/lib/estoquePDF";
import { PdfPreviewDialog } from "@/components/financeiro/contas/ContasDialogs";

const hoje = () => new Date().toISOString().slice(0, 10);
const inicioMes = () => hoje().slice(0, 8) + "01";

export function RelatorioEstoqueDialog({ open, onClose }) {
  const [tipo, setTipo] = useState("saida");
  const [dataInicio, setDataInicio] = useState(inicioMes());
  const [dataFim, setDataFim] = useState(hoje());
  const [filtro, setFiltro] = useState("");
  const [opcoes, setOpcoes] = useState([]);
  const [gerando, setGerando] = useState(false);
  const [pdfModal, setPdfModal] = useState({ open: false, blobUrl: null, filename: "" });

  useEffect(() => {
    if (open) axios.get(`${API}/estoque-relatorios/filtros-obras`).then(r => setOpcoes(r.data)).catch(() => {});
  }, [open]);

  const gerar = async () => {
    if (!dataInicio || !dataFim) { toast.error("Informe o período completo"); return; }
    if (dataInicio > dataFim) { toast.error("Data inicial maior que a final"); return; }
    setGerando(true);
    try {
      const url = tipo === "entrada"
        ? `${API}/estoque-relatorios/entradas-v2?data_inicio=${dataInicio}&data_fim=${dataFim}`
        : `${API}/estoque-relatorios/saidas-v2?data_inicio=${dataInicio}&data_fim=${dataFim}${filtro ? `&filtro=${encodeURIComponent(filtro)}` : ""}`;
      const r = await axios.get(url);
      const filtroLabel = (opcoes.find(o => o.key === filtro) || {}).label;
      const { blob, filename } = await gerarRelatorioEstoquePDF({ tipo, dados: r.data, dataInicio, dataFim, filtroLabel });
      if (pdfModal.blobUrl) URL.revokeObjectURL(pdfModal.blobUrl);
      setPdfModal({ open: true, blobUrl: URL.createObjectURL(blob), filename });
      if ((r.data.itens || []).length === 0) toast.info("Nenhuma movimentação no período — relatório gerado vazio");
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao gerar relatório")); }
    finally { setGerando(false); }
  };

  const fecharPreview = () => {
    if (pdfModal.blobUrl) URL.revokeObjectURL(pdfModal.blobUrl);
    setPdfModal({ open: false, blobUrl: null, filename: "" });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-lg" data-testid="dialog-relatorio-estoque">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-luvcorp-gold" /> Relatório PDF de Estoque</DialogTitle>
            <DialogDescription>Entradas (NF × pedido) ou Saídas (romaneios + lançamentos antigos). No filtro por obra é possível escolher o contrato com todos os aditivos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <NativeSelect value={tipo} onValueChange={setTipo} data-testid="select-tipo-relatorio"
              options={[{ value: "saida", label: "Saídas (por obra ou todas)" }, { value: "entrada", label: "Entradas (compras)" }]} />
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">De</Label><Input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} data-testid="input-rel-inicio" /></div>
              <div><Label className="text-xs">Até</Label><Input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} data-testid="input-rel-fim" /></div>
            </div>
            {tipo === "saida" && (
              <div>
                <Label className="text-xs">Obra (contrato + aditivos) ou centro de custo</Label>
                <NativeSelect value={filtro} onValueChange={setFiltro} placeholder="Todas as obras" data-testid="select-filtro-obra" searchable
                  options={[{ value: "", label: "Todas as obras" },
                    ...opcoes.map(o => ({ value: o.key, label: (o.tipo === "grupo" ? "★ " : "   ") + o.label }))]} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Fechar</Button>
            <Button onClick={gerar} disabled={gerando} className="bg-luvcorp-navy text-white" data-testid="btn-gerar-relatorio">
              {gerando ? "Gerando…" : "Gerar PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PdfPreviewDialog pdfModal={pdfModal} onClose={fecharPreview} titulo="Relatório de Estoque" />
    </>
  );
}
